<template>
  <div>
    <v-dialog persistent v-model="saving" max-width="420">
      <v-card :loading="true">
        <div class="mt-5 pa-2 title text-center">
          <p>
            {{ `Saving ${entityName}s ...` }}
          </p>
          <p>
            This could take a few moments. <br />Do not close the browser
            window.
          </p>
        </div>
      </v-card>
    </v-dialog>
    <v-data-table
      :expanded="expanded"
      :headers="headerList"
      :item-key="importKeyName"
      :items="entityList"
      class="transparent"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:item="{ headers, item, isExpanded }">
        <tr
          :class="`row-clickable upload-preview ${
            getDiffList(item).length ? 'primary--text' : ''
          }`"
          :data-cy="`${item[importKeyName]}-upload-preview`"
          @click="isExpanded ? (expanded = []) : (expanded = [item])"
        >
          <template v-for="header in headers">
            <td v-if="header.value === 'hasChanges'" :key="header.value">
              {{ getDiffList(item).length ? "Yes" : "No" }}
            </td>
            <td :key="header.value" v-else>
              {{ item[header.value] }}
            </td>
          </template>
        </tr>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="py-8">
          <v-divider></v-divider>
          <v-data-table
            :headers="diffHeaderList"
            :items="getDiffList(item)"
            class="transparent"
            disable-pagination
            hide-default-footer
            no-data-text="No updates will occur"
          >
            <template v-slot:item="{ headers, item }">
              <tr>
                <template v-for="header in headers">
                  <td
                    :data-cy="`${item[headers[0].value]}-${camelCase(
                      header.text
                    )}-preview`"
                    :key="header.value"
                  >
                    {{ parseValueForDisplay(item[header.value]) }}
                  </td>
                </template>
              </tr>
            </template>
          </v-data-table>
          <v-divider></v-divider>
        </td>
      </template>

      <template v-slot:footer>
        <v-divider class="mb-4"></v-divider>
        <v-container class="">
          <v-alert
            dense
            outlined
            color="yellow darken-4"
            v-if="attributesBeingNulled.length"
          >
            <v-icon small color="yellow darken-4" class="mr-2">
              fas fa-exclamation-triangle
            </v-icon>
            <span class="mt-4">
              The following values will be cleared for 1 or more users:
            </span>
            <ul>
              <li v-for="attribute in attributesBeingNulled" :key="attribute">
                {{ attributeMapByName[attribute].label }}
              </li>
            </ul>
            <div class="d-flex justify-end" v-if="!attributeNullAccepted">
              <v-btn
                @click="attributeNullAccepted = true"
                outlined
                small
                color="yellow darken-4 mb-3"
              >
                Accept
              </v-btn>
            </div>
          </v-alert>
        </v-container>
        <div class="d-flex justify-space-between">
          <v-btn outlined color="primary" small @click="$emit('canceled')"
            >Back</v-btn
          >
          <v-tooltip
            :disabled="!attributesBeingNulled.length || attributeNullAccepted"
            top
          >
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-btn
                  data-cy="submit-preview-data"
                  outlined
                  color="primary"
                  :disabled="
                    !!attributesBeingNulled.length && !attributeNullAccepted
                  "
                  small
                  @click="submit()"
                  >Submit ({{ entityList.length }})</v-btn
                >
              </div>
            </template>
            <span> Value clearing must be accepted first. </span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { get, camelCase, isEqual, keyBy, reduce, uniq } from "lodash";
import { mapGetters, mapState } from "vuex";
import axios from "axios";

import { getLabel } from "../../../../lib/componentUtil";

export default {
  computed: {
    attributeList() {
      const { entityName } = this;

      return this.attributeFilter({ entityName });
    },
    attributeMapByName() {
      const { attributeList } = this;

      return keyBy(attributeList, "name");
    },
    attributeNameList() {
      const { attributeList } = this;

      return attributeList.map(definition => definition.name);
    },
    attributesBeingNulled() {
      const {
        entityList,
        getDiffList,
        nullValueForDisplay,
        parseValueForDisplay,
      } = this;

      if (!entityList || !entityList.length) {
        return false;
      }

      const nulledAttributeList = entityList.reduce((acc, entity) => {
        const entityDiffList = getDiffList(entity);

        if (!entityDiffList) {
          return acc;
        }

        const entityNulledDiffList = entityDiffList.filter(
          ({ valueNew }) =>
            parseValueForDisplay(valueNew) === nullValueForDisplay
        );
        const entityNulledAttributeList = entityNulledDiffList.map(
          ({ attribute }) => attribute
        );

        return uniq([...acc, ...entityNulledAttributeList]);
      }, []);

      return nulledAttributeList;
    },
    entityListExisting() {
      const { entityName } = this;

      return get(this.$store.state, `${entityName}.list`);
    },
    entityMapByImportKey() {
      const { entityListExisting, importKeyName } = this;

      if (!importKeyName) {
        return {};
      }

      const userMap = {};
      for (const user of entityListExisting) {
        const importKeyValue = user[importKeyName];
        if (importKeyValue) {
          userMap[importKeyValue.toLowerCase()] = user;
        }
      }

      return userMap;
    },
    entityMapByPrimaryKey() {
      const { entityListExisting, primaryKeyName } = this;

      if (!primaryKeyName) {
        return {};
      }

      const entityMap = {};
      for (const entity of entityListExisting) {
        const primaryKeyValue = entity[primaryKeyName];
        if (primaryKeyValue) {
          entityMap[primaryKeyValue] = entity;
        }
      }

      return entityMap;
    },
    headerList() {
      return [
        { text: "Has Changes", value: "hasChanges" },
        { text: getLabel(this.importKey), value: this.importKeyName },
      ];
    },
    importKey() {
      const { attributeList } = this;
      const importKey = attributeList.find(attribute => !!attribute.importKey);
      return importKey;
    },
    importKeyName() {
      const { importKey } = this;

      return get(importKey, "name");
    },
    primaryKey() {
      const { attributeList } = this;

      return attributeList.find(attribute => !!attribute.primaryKey);
    },
    primaryKeyName() {
      const { primaryKey } = this;

      return get(primaryKey, "name");
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
    }),
    ...mapState({
      organizationId: state => state.session.organizationId,
    }),
  },
  data() {
    return {
      attributeNullAccepted: false,
      diffHeaderList: [
        { text: "Name", value: "attribute" },
        { text: "Old Value", value: "valueOld" },
        { text: "New Value", value: "valueNew" },
      ],
      expanded: [],
      nullValueForDisplay: "<no value>",
      saving: false,
    };
  },
  methods: {
    camelCase,
    async entityCreate(attributeMap) {
      return this.$store.dispatch(`${this.entityName}/create`, attributeMap);
    },
    async entityUpdate([id, attributeMap]) {
      return this.$store.dispatch(`${this.entityName}/update`, [
        id,
        attributeMap,
      ]);
    },
    getDiffList(entity) {
      if (!entity) {
        return [];
      }

      const { importKeyName, primaryKeyName } = this;

      const importKey = entity[importKeyName];
      let existingEntity = this.getExistingEntity(importKey);

      // Fall back to primary key lookup if import key lookup fails
      if (!existingEntity && primaryKeyName) {
        const primaryKeyValue = entity[primaryKeyName];
        existingEntity = this.getExistingEntityByPrimaryKey(primaryKeyValue);
      }

      if (!existingEntity) {
        existingEntity = {};
      }

      const diff = reduce(
        entity,
        (acc, valueNew, attribute) => {
          let valueOld = existingEntity[attribute];

          if (valueOld === valueNew) {
            return acc;
          }

          const emptyList = [null, undefined];
          if (emptyList.includes(valueOld) && emptyList.includes(valueNew)) {
            return acc;
          }

          if (Array.isArray(valueOld) && Array.isArray(valueNew)) {
            if (isEqual(valueOld, valueNew)) {
              return acc;
            }
          }

          acc.push({
            attribute,
            valueNew,
            valueOld,
          });

          return acc;
        },
        []
      );

      return diff;
    },
    getExistingEntity(importKey) {
      const { entityMapByImportKey } = this;

      if (!importKey) {
        return undefined;
      }

      let entityExisting = entityMapByImportKey[importKey.toLowerCase()];

      return entityExisting;
    },
    getExistingEntityByPrimaryKey(primaryKeyValue) {
      const { entityMapByPrimaryKey } = this;

      if (!primaryKeyValue) {
        return undefined;
      }

      return entityMapByPrimaryKey[primaryKeyValue];
    },
    parseValueForDisplay(value) {
      const { nullValueForDisplay } = this;

      if (value === null || value === undefined) {
        return nullValueForDisplay;
      }

      if (typeof value === "string" && value === "") {
        return nullValueForDisplay;
      }

      return value;
    },
    async submit() {
      this.saving = true;

      let resultList;
      try {
        ({
          data: { result: resultList },
        } = await axios({
          method: "POST",
          url: `/api/${this.entityName}/batch`,
          data: this.entityList,
        }));
      } catch (e) {
        console.error(e);
        this.saving = false;
        this.$notifyError({
          message: `Error while running ${this.entityName} imports`,
        });
        return;
      }

      try {
        await this.$store.dispatch(`${this.entityName}/list`);
      } catch (e) {
        console.error(e);
        this.saving = false;
        this.$notifyError({
          message: `Error while running ${this.entityName} batch operation`,
        });
        return;
      }

      this.saving = false;

      this.$emit("submitted", resultList);
    },
  },
  props: {
    entityList: {
      required: true,
      type: Array,
    },
    entityName: {
      required: true,
      type: String,
    },
  },
  watch: {
    entityList() {
      this.attributeNullAccepted = false;
    },
  },
};
</script>

<style scoped>
.row-clickable {
  cursor: pointer;
}
</style>
